import { ComputerInfoDetails } from '@exam-center/exam-location-management';
import {
  type ExamRoomRequestComputerAttributes,
  useAdminGetDownloadSignedUrlForExamRoomComputerInfoLazyQuery,
} from '@exam-center/graphql';
import { useEffect, useMemo, useState } from 'react';

import { useAdminExamLocationDetail } from '../../providers';

export default function AdminExamLocationRoomComputerInfoDetails() {
  const { examRoomInfo } = useAdminExamLocationDetail();

  const attrs = examRoomInfo?.examRoomComputerInfo?.info as
    ExamRoomRequestComputerAttributes | null | undefined;

  const [getDownloadSignedUrls] = useAdminGetDownloadSignedUrlForExamRoomComputerInfoLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [signedUrlMap, setSignedUrlMap] = useState<Record<string, string>>({});

  const fileNames = useMemo(() => {
    const names: string[] = [];
    if (!attrs) return names;

    if (Array.isArray(attrs.computerDetails?.computerSpecImages)) {
      names.push(...(attrs.computerDetails?.computerSpecImages ?? []));
    }
    if (Array.isArray(attrs.computerDetails?.computerSeatImages)) {
      names.push(...(attrs.computerDetails?.computerSeatImages ?? []));
    }
    if (Array.isArray(attrs.internetDetails?.speedTestImages)) {
      names.push(...(attrs.internetDetails?.speedTestImages ?? []));
    }
    if (Array.isArray(attrs.securityDetails?.timeSyncImages)) {
      names.push(...(attrs.securityDetails?.timeSyncImages ?? []));
    }
    if (Array.isArray(attrs.securityDetails?.robotTestImages)) {
      names.push(...(attrs.securityDetails?.robotTestImages ?? []));
    }

    return Array.from(new Set(names)).filter(Boolean) as string[];
  }, [attrs]);

  useEffect(() => {
    if (!fileNames.length) return;

    (async () => {
      const result = await getDownloadSignedUrls({
        variables: {
          getDownloadSignedUrlForExamRoomComputerInfoInput: {
            fileNames,
            examRoomId: examRoomInfo.id,
          },
        },
        fetchPolicy: 'no-cache',
      });

      const map = (result.data?.ecAdminGetDownloadSignedUrlForExamRoomComputerInfo?.signedUrls ??
        {}) as Record<string, string>;
      setSignedUrlMap(map);
    })();
  }, [fileNames, getDownloadSignedUrls, examRoomInfo.id]);

  useEffect(() => {
    const frameContent = document.getElementById('frame-content');
    if (frameContent) {
      frameContent.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, []);

  const resolved = useMemo(() => {
    if (!attrs) return attrs;
    const resolve = (names?: string[] | null) =>
      (names ?? []).map((n) => (n ? signedUrlMap[n] : '')).filter((u): u is string => Boolean(u));

    return {
      ...attrs,
      computerDetails: attrs.computerDetails
        ? {
            ...attrs.computerDetails,
            computerSpecImages: resolve(attrs.computerDetails.computerSpecImages),
            computerSeatImages: resolve(attrs.computerDetails.computerSeatImages),
          }
        : null,
      internetDetails: attrs.internetDetails
        ? {
            ...attrs.internetDetails,
            speedTestImages: resolve(attrs.internetDetails.speedTestImages),
          }
        : null,
      securityDetails: attrs.securityDetails
        ? {
            ...attrs.securityDetails,
            timeSyncImages: resolve(attrs.securityDetails.timeSyncImages),
            robotTestImages: resolve(attrs.securityDetails.robotTestImages),
          }
        : null,
    } as ExamRoomRequestComputerAttributes;
  }, [attrs, signedUrlMap]);

  const cd = resolved?.computerDetails;
  const id = resolved?.internetDetails;
  const sd = resolved?.securityDetails;

  return <ComputerInfoDetails computerDetails={cd} internetDetails={id} securityDetails={sd} />;
}
